/**
 * 文本文档存储提供者
 * 提供文本文档的基础数据库操作
 *
 * 该类仅处理数据库操作，不包含业务逻辑
 */
import { dbManager } from '../database/index.js'; // 导入共享数据库实例
/**
 * 文本文档存储提供者
 * 实现基础的数据库操作
 */
export class TextDocumentStorageProvider {
    /**
     * 私有构造函数
     */
    constructor() {
        this.initialized = false;
        this.initPromise = null;
        // The constructor is now empty.
    }
    /** Dynamic getter for the textDocuments table */
    get textDocuments() {
        return dbManager.textDocuments;
    }
    /** Dynamic getter for the textParagraphs table */
    get textParagraphs() {
        return dbManager.textParagraphs;
    }
    /**
     * 获取单例实例
     */
    static getInstance() {
        if (!TextDocumentStorageProvider.instance) {
            TextDocumentStorageProvider.instance = new TextDocumentStorageProvider();
        }
        return TextDocumentStorageProvider.instance;
    }
    /**
     * 初始化提供者
     * 确保数据库连接就绪
     */
    async initialize() {
        if (this.initialized)
            return;
        if (this.initPromise)
            return this.initPromise;
        this.initPromise = (async () => {
            try {
                // Initialization logic likely happens in constructor or via other means.
                // This async method might be for ensuring readiness or other async setup.
                // Removing previously added problematic lines and the console.warn check.
                this.initialized = true;
            }
            catch (error) {
                console.error('❌ Error during TextDocumentStorageProvider initialization:', error);
                this.initialized = false;
                throw error;
            }
            finally {
                this.initPromise = null;
            }
        })();
        return this.initPromise;
    }
    /**
     * 确保初始化
     */
    async ensureInitialized() {
        if (!this.initialized) {
            await this.initialize();
        }
    }
    /**
     * 添加文档
     * @param document 文档对象（不含ID）
     * @returns 新添加文档的ID
     */
    async addDocument(document) {
        await this.ensureInitialized();
        const numericId = await this.textDocuments.add(document);
        return { id: numericId, type: 'text' };
    }
    /**
     * 批量添加段落
     * @param paragraphs 段落数组
     */
    async addParagraphs(paragraphs) {
        await this.ensureInitialized();
        await this.textParagraphs.bulkAdd(paragraphs);
    }
    /**
     * 获取文档
     * @param identity 文档标识符
     * @returns 文档对象
     */
    async getDocument(identity) {
        await this.ensureInitialized();
        return await this.textDocuments.get(identity.id);
    }
    /**
     * 获取文档的所有段落
     * @param identity 文档标识符
     * @returns 段落数组
     */
    async getParagraphs(identity) {
        await this.ensureInitialized();
        return await this.textParagraphs.where('textId').equals(identity.id).sortBy('index');
    }
    /**
     * 获取所有文档
     * @returns 文档数组
     */
    async getAllDocuments() {
        await this.ensureInitialized();
        return await this.textDocuments.orderBy('timestamp').reverse().toArray();
    }
    /**
     * 获取文档列表（支持分页和搜索）
     * @param options 查询选项 { limit, offset, searchText, sortBy }
     * @returns 文档数组
     */
    async getDocuments(options = {}) {
        await this.ensureInitialized();
        // Set defaults
        const { limit = 20, offset = 0, searchText, sortBy = 'timestamp-desc' } = options;
        // Start query building from the Collection
        let collection = this.textDocuments.toCollection();
        // Apply filter FIRST
        if (searchText) {
            const lowerSearchText = searchText.toLowerCase();
            collection = collection.filter(doc => doc.title?.toLowerCase().includes(lowerSearchText) ||
                doc.preview?.toLowerCase().includes(lowerSearchText));
        }
        // Fetch ALL matching documents into memory FIRST
        const results = await collection.toArray();
        // Apply sorting to the in-memory array
        if (sortBy === 'time-asc') {
            results.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        }
        else {
            // Default to 'timestamp-desc'
            results.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        }
        // Apply pagination to the sorted in-memory array
        return results.slice(offset, offset + limit);
    }
    /**
     * 更新文档
     * @param identity 文档标识符
     * @param changes 要更新的字段
     * @returns 更新的记录数
     */
    async updateDocument(identity, changes) {
        await this.ensureInitialized();
        await this.textDocuments.update(identity.id, changes);
        return identity;
    }
    /**
     * 删除文档
     * @param identity 文档标识符
     */
    async deleteDocument(identity) {
        await this.ensureInitialized();
        await this.textDocuments.delete(identity.id);
    }
    /**
     * 删除文档的所有段落
     * @param identity 文档标识符
     * @returns 删除的段落数量
     */
    async deleteParagraphs(identity) {
        await this.ensureInitialized();
        return await this.textParagraphs.where({ textId: identity.id }).delete();
    }
    /**
     * 获取文档数量
     * @returns 文档数量
     */
    async getDocumentCount() {
        await this.ensureInitialized();
        return await this.textDocuments.count();
    }
    /**
     * 在事务中执行操作
     * @param callback 事务回调函数
     * @returns 回调函数的结果
     */
    async transaction(callback) {
        await this.ensureInitialized();
        return dbManager.transaction('rw', ['textDocuments', 'textParagraphs'], async () => {
            return await callback();
        });
    }
    /**
     * 获取文档的完整结构（调试用）
     * @param identity 文档标识符
     * @returns 完整的文档对象
     */
    async getDocumentRaw(identity) {
        await this.ensureInitialized();
        return await this.textDocuments.get(identity.id);
    }
}
// No longer cache table instances. They will be accessed dynamically.
// 单例模式
TextDocumentStorageProvider.instance = null;
//# sourceMappingURL=TextDocumentStorageProvider.js.map